"use client"

import { X, Sparkles } from "lucide-react"
import { Check } from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import { dictionary } from "../../utils/dictionary"

interface SwitchToPlanLiteModalProps {
  isOpen: boolean
  onClose: () => void
  onAccept: () => void
  onCancel: () => void
  variant: "discount50" | "regular" | "free"
  isLoading: boolean
  isLoadingCancel: boolean
}

export function SwitchToPlanLiteModal({ isOpen, onClose, onAccept, onCancel, variant, isLoading, isLoadingCancel }: SwitchToPlanLiteModalProps) {
  const { language } = useAuth();
  if (!isOpen) return null

  const getDiscountConfig = () => {
    switch (variant) {
      case "discount50":
        return {
          badgeText: dictionary["special-price"][language],
          description:
            dictionary["to-compensate-the-remaining-days-of-your-previous-plan-we-give-you-a-50-discount-in-the-first-month"][language],
          priceText: dictionary["plan-lite-first-month"][language],
          afterText: dictionary["then-19-per-month"][language],
        }
      case "free":
        return {
          badgeText: dictionary["special-price"][language],
          description:
            dictionary["to-compensate-the-remaining-days-of-your-previous-plan-we-give-you-a-100-discount-in-the-first-month"][language],
          priceText: dictionary["free-first-month"][language],
          afterText: dictionary["then-19-per-month"][language],
        }
      case "regular":
      default:
        return {
          badgeText: null,
          description: null,
          priceText: dictionary["19-per-month"][language],
          afterText: null,
        }
    }
  }

  const config = getDiscountConfig()
  const hasDiscount = variant !== "regular"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-3xl shadow-lg w-full max-w-md mx-4 p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
        <div className="space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
          </div>

          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">{dictionary["wait-we-have-a-special-offer"][language]}</h2>
            <p className="text-gray-600">
              {dictionary["before-you-leave-we-offer-you-a-lite-plan-for-only"][language]}
              <span className="text-green-600 font-semibold">{dictionary["$19/mes"][language]}</span>?
            </p>
          </div>

          <div className="bg-green-50 rounded-lg p-4 space-y-4">
            {hasDiscount && (
              <div className="relative bg-green-500 rounded-lg p-4 text-white">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-red-600 text-white text-[10px] font-bold px-3 py-1 rounded">{config.badgeText}</span>
                </div>

                <div className="flex items-start gap-3 mt-2">
                  <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="text-xs font-medium">{config.description}</p>
                </div>
              </div>
            )}
            <div className="text-center">
              <p className="font-semibold text-gray-900">{config.priceText}</p>
              <p className="text-sm text-gray-600 mt-1">{config.afterText}</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">{dictionary["keep-all-your-created-pages-active"][language]}</p>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">{dictionary["no-additional-credits-next-month"][language]}</p>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">{dictionary["access-to-the-platform"][language]}</p>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">
                  {dictionary["you-can-buy-credits-or-upgrade-your-plan-when-you-want"][language]}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              disabled={isLoading}
              onClick={onAccept}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              {isLoading ? dictionary["loading"][language] : dictionary["yes-i-want-the-lite-plan"][language]}
            </button>

            <button
              disabled={isLoading || isLoadingCancel}
              onClick={onCancel}
              className="w-full text-gray-600 hover:text-gray-800 font-medium text-sm transition-colors"
            >
              {isLoadingCancel ? dictionary["loading"][language] : dictionary["no-thanks-i-want-to-cancel-my-access-to-fluxi"][language]}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
